import type { ProductColor } from '@/types'

// Deterministic IDR formatting (identical on server and client): 285000 -> "Rp 285.000"
export const formatIDR = (value: number): string => {
    const rounded = Math.round(Number.isFinite(value) ? value : 0)
    const digits = Math.abs(rounded).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')

    return `${rounded < 0 ? '-' : ''}Rp ${digits}`
}

const NAMED_COLORS: Array<[RegExp, string]> = [
    [/blush|pink|rose|sakura/i, '#F3C9CF'],
    [/berry|raspberry/i, '#C96B7C'],
    [/sage|matcha|green|olive|clover/i, '#A9BA9D'],
    [/mint|pistachio/i, '#CFE3D0'],
    [/lilac|lavender|purple|violet/i, '#D9C6E6'],
    [/sky|blue/i, '#BFD5E6'],
    [/sunshine|yellow|butter|lemon/i, '#F6D77B'],
    [/orange|maple|ginger|peach/i, '#E2A66F'],
    [/rust|terracotta/i, '#B9653F'],
    [/caramel|honey|tan/i, '#C8956A'],
    [/chocolate|cocoa|brown/i, '#7A5640'],
    [/biscuit|beige|sand|oat|latte/i, '#DCC5A8'],
    [/grey|gray|silver|dove/i, '#BDB5AE'],
    [/black|charcoal/i, '#3B3532'],
    [/red|cherry/i, '#C9474F'],
    [/white|snow|cream|vanilla|ivory|moon/i, '#F4EBDD']
]

export const normalizeHex = (value: string | null | undefined): string | null => {
    if (!value) return null

    const match = value.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i)

    if (!match) return null

    const hex = match[1].length === 3
        ? match[1].split('').map(char => char + char).join('')
        : match[1]

    return `#${hex.toUpperCase()}`
}

// Product colours are stored as "Label #HEX" strings, the hex part is optional.
export const parseColor = (value: string): ProductColor => {
    const match = value.match(/#([0-9a-f]{6}|[0-9a-f]{3})\b/i)
    const hex = match ? normalizeHex(match[0]) : null
    const label = (match ? value.replace(match[0], '') : value).replace(/\s+/g, ' ').trim()
    const named = NAMED_COLORS.find(([pattern]) => pattern.test(label))

    return {
        label: label || hex || 'Colour',
        hex: hex ?? (named ? named[1] : null)
    }
}

export const parseJsonArray = (value: unknown): Array<string> => {
    if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string')
    if (typeof value !== 'string' || !value.trim()) return []

    try {
        const parsed = JSON.parse(value)

        return Array.isArray(parsed)
            ? parsed.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
            : []
    }
    catch {
        return value.split(',').map(item => item.trim()).filter(Boolean)
    }
}

export const stockLabel = (stock: number, madeToOrder: boolean): { label: string, tone: 'ok' | 'low' | 'out' | 'custom' } => {
    if (madeToOrder) return { label: 'Made to order', tone: 'custom' }
    if (stock <= 0) return { label: 'Sold out', tone: 'out' }
    if (stock <= 3) return { label: `Only ${stock} left`, tone: 'low' }

    return { label: `In stock, ${stock} available`, tone: 'ok' }
}
